import ThemedElement from "shared/components/ThemedElement";
import React from "react";
import PropTypes from "prop-types";
import { Grid, Paper, Box, CircularProgress, Alert, Snackbar, LinearProgress } from "@mui/material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import ConfirmacionFacturas from "./ConfirmacionFacturas";
import { useFormFacturas } from "../../hooks/useFormFacturas";

const colors = {
  primary: "#1e293b",
  success: "#27ae60",
  error: "#e74c3c",
  info: "#2980b9",
  background: "#f8f9fa",
  border: "#dfe6e9",
  warning: "#ed6c02",
};

const FormFacturas = ({ onImportSuccess, onError }) => {
  const {
    selectedFile,
    importing,
    importResult,
    showConfirmModal,
    validationErrors,
    validating,
    snackbar,
    loading: userLoading,
    importProgress,
    duplicateInFile,
    duplicateInDatabase,
    getCurrentUsername,
    handleFileChange,
    handleImport,
    handleCloseModal,
    clearSelectedFile,
    handleCloseSnackbar,
    setSnackbar,
  } = useFormFacturas();

  const onFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      await handleFileChange(file, event);
    }
  };

  const onImportClick = async () => {
    await handleImport(onImportSuccess, onError);
  };

  const handleDownloadTemplate = () => {
    try {
      const templateUrl = "/templates/plantilla_facturas.xlsx";

      const link = document.createElement("a");
      link.href = templateUrl;
      link.download = "plantilla_facturas.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: "Descargando plantilla de ejemplo...",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al descargar plantilla:", error);
      setSnackbar({
        open: true,
        message: "Error al descargar la plantilla. Contacte al administrador.",
        severity: "error",
      });
    }
  };

  if (userLoading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <MDTypography variant="body2" ml={2} sx={{ color: "text.secondary" }}>
          Cargando información del usuario...
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: `2px solid ${colors.border}`,
          backgroundColor: "white",
          mb: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: colors.primary,
            color: "white",
            p: 3,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <CloudUploadIcon fontSize="large" sx={{ color: "#fff" }} />
              <Box>
                <MDTypography variant="h5" fontWeight="bold" color="white">
                  Importar Facturas desde Excel
                </MDTypography>
                <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                  Usuario: {getCurrentUsername()}
                </MDTypography>
              </Box>
            </Box>

            <MDButton
              variant="outlined"
              onClick={handleDownloadTemplate}
              startIcon={<DownloadIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                color: "white",
                borderColor: "rgba(255,255,255,0.5)",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Descargar Plantilla
            </MDButton>
          </Box>
        </Box>

        <Box px={3} py={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: `2px dashed ${
                    selectedFile
                      ? colors.success
                      : validationErrors.length > 0
                      ? colors.error
                      : colors.border
                  }`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  backgroundColor: selectedFile
                    ? `${colors.success}08`
                    : validationErrors.length > 0
                    ? `${colors.error}08`
                    : colors.background,
                  transition: "all 0.2s",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: selectedFile
                      ? colors.success
                      : validationErrors.length > 0
                      ? colors.error
                      : colors.info,
                    backgroundColor: selectedFile
                      ? `${colors.success}10`
                      : validationErrors.length > 0
                      ? `${colors.error}10`
                      : "#fff",
                  },
                }}
                onClick={() => document.getElementById("file-input")?.click()}
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={onFileSelect}
                  style={{ display: "none" }}
                  disabled={validating}
                />
                {validating ? (
                  <>
                    <CircularProgress size={48} sx={{ mb: 1 }} />
                    <MDTypography variant="h6" gutterBottom sx={{ color: colors.info }}>
                      Validando archivo...
                    </MDTypography>
                  </>
                ) : selectedFile ? (
                  <>
                    <CheckCircleIcon sx={{ fontSize: 48, color: colors.success, mb: 1 }} />
                    <MDTypography variant="h6" gutterBottom sx={{ color: colors.success }}>
                      Archivo válido
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ color: "text.secondary" }}>
                      {selectedFile.name}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      display="block"
                      sx={{ color: "text.secondary", mt: 1 }}
                    >
                      ({Math.round(selectedFile.size / 1024)} KB)
                    </MDTypography>
                    <MDButton
                      variant="text"
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSelectedFile();
                      }}
                      sx={{ mt: 1 }}
                    >
                      Cambiar archivo
                    </MDButton>
                  </>
                ) : (
                  <>
                    <DescriptionIcon
                      sx={{
                        fontSize: 48,
                        color: validationErrors.length > 0 ? colors.error : colors.info,
                        mb: 1,
                      }}
                    />
                    <MDTypography
                      variant="h6"
                      sx={{ color: validationErrors.length > 0 ? colors.error : colors.info }}
                      gutterBottom
                    >
                      {validationErrors.length > 0
                        ? "Archivo inválido"
                        : "Seleccionar archivo Excel"}
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ color: "text.secondary" }}>
                      Haga clic para buscar un archivo .xlsx o .xls
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      display="block"
                      sx={{ color: "text.secondary", mt: 1 }}
                    >
                      Columnas requeridas: # Factura, NIT, Empresa, Valor, Código (TODAS son
                      obligatorias)
                    </MDTypography>

                    {/* Mostrar errores de validación */}
                    {validationErrors.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          textAlign: "left",
                          bgcolor: "#ffebee",
                          p: 1.5,
                          borderRadius: 1,
                        }}
                      >
                        <MDTypography variant="caption" color="error" fontWeight="bold">
                          Errores de validación:
                        </MDTypography>
                        {validationErrors.slice(0, 5).map((err, idx) => (
                          <MDTypography key={idx} variant="caption" color="error" display="block">
                            • {err}
                          </MDTypography>
                        ))}
                        {validationErrors.length > 5 && (
                          <MDTypography
                            variant="caption"
                            color="error"
                            display="block"
                            sx={{ mt: 1 }}
                          >
                            • ... y {validationErrors.length - 5} errores más
                          </MDTypography>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 4,
              mb: 2,
              p: 2.5,
              bgcolor: "#fff8e1",
              borderRadius: 2,
              border: "1px solid #ffe082",
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <HelpOutlineIcon sx={{ fontSize: 22, color: colors.warning }} />
              <MDTypography variant="subtitle1" fontWeight="bold" sx={{ color: colors.warning }}>
                Instrucciones para la importación
              </MDTypography>
            </Box>

            <MDTypography variant="body2" component="div" sx={{ color: "text.secondary" }}>
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                <li>El archivo debe ser tipo Excel (.xlsx o .xls)</li>
                <li>
                  Debe contener las columnas: <strong># Factura</strong>, <strong>NIT</strong>,{" "}
                  <strong>Empresa</strong>, <strong>Valor</strong>, <strong>Código</strong> (TODAS
                  son obligatorias)
                </li>
                <li>Las facturas se importarán con estado &quot;pendiente&quot;</li>
                <li>El archivo será validado automáticamente al seleccionarlo</li>
                <li>
                  La primera fila debe contener los encabezados:{" "}
                  <strong># Factura, NIT, Empresa, Valor, Código</strong>
                </li>
                <li>Las filas siguientes contienen los datos de cada factura</li>
                <li>
                  La columna <strong>Valor</strong> debe ser numérica (sin puntos ni comas como
                  separadores de miles)
                </li>
                <li>
                  La columna <strong>Código</strong> es{" "}
                  <ThemedElement component="strong" style={{ color: colors.error }}>
                    OBLIGATORIA
                  </ThemedElement>{" "}
                  y debe contener un identificador único por empresa
                </li>
                <li>
                  <strong>No se permiten facturas duplicadas</strong> (ni dentro del mismo archivo
                  ni que ya existan en el sistema)
                </li>
                <li>
                  <strong>Recomendación:</strong> Descargue la plantilla de ejemplo para asegurar el
                  formato correcto
                </li>
              </ul>
            </MDTypography>

            <Box
              sx={{
                mt: 2,
                pt: 1.5,
                borderTop: "1px dashed #ffe082",
              }}
            >
              <MDTypography variant="caption" sx={{ color: "text.secondary" }}>
                <strong>Ejemplo de fila válida:</strong>{" "}
                <code style={{ backgroundColor: "#fff", padding: "2px 6px", borderRadius: 4 }}>
                  FAC01010, 900123456, Empresa SAS, 1500000, EMP-001
                </code>
              </MDTypography>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <MDButton
              variant="contained"
              fullWidth
              disabled={!selectedFile || importing || validating}
              onClick={onImportClick}
              size="large"
              sx={{
                py: 1.5,
                fontSize: "1rem",
                color: "#ffffffff",
                fontWeight: "bold",
                borderRadius: 1,
                bgcolor: selectedFile ? colors.success : colors.border,
                "&:hover": {
                  bgcolor: selectedFile ? "#1e8449" : colors.border,
                },
                "&:disabled": {
                  bgcolor: colors.border,
                  color: "#ffffffff",
                },
              }}
            >
              {importing ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1.5, color: "#ffffffff" }} />
                  Procesando...
                </>
              ) : validating ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1.5, color: "#ffffffff" }} />
                  Validando...
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ mr: 1.5, color: "#ffffffff" }} />
                  Procesar Facturas
                </>
              )}
            </MDButton>
          </Box>
        </Box>
      </Paper>

      <ConfirmacionFacturas
        open={showConfirmModal}
        onClose={handleCloseModal}
        importing={importing}
        importResult={importResult}
        fileName={selectedFile?.name}
        importProgress={importProgress}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "info" ? 3000 : 6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{
            width: "100%",
            minWidth: "300px",
            backgroundColor:
              snackbar.severity === "success"
                ? "#f0fff4"
                : snackbar.severity === "error"
                ? "#fff5f5"
                : snackbar.severity === "info"
                ? "#e3f2fd"
                : "#fff8e1",
            color:
              snackbar.severity === "success"
                ? "#276749"
                : snackbar.severity === "error"
                ? "#c53030"
                : snackbar.severity === "info"
                ? "#0b4f6c"
                : "#856404",
            "& .MuiAlert-icon": {
              color:
                snackbar.severity === "success"
                  ? "#276749"
                  : snackbar.severity === "error"
                  ? "#c53030"
                  : snackbar.severity === "info"
                  ? "#0b4f6c"
                  : "#856404",
            },
            border:
              snackbar.severity === "success"
                ? "1px solid #c6f6d5"
                : snackbar.severity === "error"
                ? "1px solid #fed7d7"
                : snackbar.severity === "info"
                ? "1px solid #b3e5fc"
                : "1px solid #ffe082",
          }}
        >
          <MDTypography variant="body1" fontWeight="bold">
            {snackbar.severity === "success"
              ? "¡Éxito!"
              : snackbar.severity === "error"
              ? "Error"
              : snackbar.severity === "info"
              ? "Información"
              : "Aviso"}
          </MDTypography>
          <MDTypography variant="body2">{snackbar.message}</MDTypography>
          {snackbar.severity === "info" && importProgress > 0 && importProgress < 100 && (
            <LinearProgress
              variant="determinate"
              value={importProgress}
              sx={{ mt: 1, height: 4, borderRadius: 2 }}
            />
          )}
        </Alert>
      </Snackbar>
    </>
  );
};

FormFacturas.propTypes = {
  onImportSuccess: PropTypes.func,
  onError: PropTypes.func,
};

FormFacturas.defaultProps = {
  onImportSuccess: null,
  onError: null,
};

export default FormFacturas;
